import { DynamicDrawUsage, InstancedMesh, Matrix4, Vector3 } from 'three';
import { hexToLinear } from '../../core/color';
import type { WorldFeature } from '../types';
import { SURFACE } from '../mesh/attributes';
import { createMeshBuilder } from '../mesh/meshBuilder';
import { octahedronSolid } from '../fabric/solids';
import { createCitizenBatch } from './citizens/citizenBatch';
import { citizenGeometry } from './citizens/citizenShapes';
import { clipPoseAt, clipPose, skinnedVertex } from './citizens/citizenPose';
import { planActivityGround } from './activityPlan';
import { streetInstances } from './streetAsset';

export const feedingFeature: WorldFeature = {
  id: 'pigeon-feeder',
  label: 'Feeding the Dam pigeons',
  weight: 0.2,
  build(ctx) {
    const asset = ctx.citizens,
      crowd = ctx.lifePlan?.crowd;
    if (!asset || !crowd) return { objects: [], animated: [], obstacles: [], counts: {} };
    const feeding = planActivityGround(ctx.layout, crowd).flatMap((g) =>
      g.feeding ? [g.feeding] : []
    );
    const figures = asset.figures.filter((f) => f.trade === 'pigeon_feeder');
    if (!feeding.length || !figures.length)
      return { objects: [], animated: [], obstacles: [], counts: {} };
    ctx.materials.setCitizenPoses(asset.poses, asset.bones.length);
    const batch = createCitizenBatch(
      ctx.materials.painted(),
      figures.map((f) => citizenGeometry(asset, f.lods[0]!)),
      feeding.length,
      'dam-pigeon-feeder'
    );
    const builder = createMeshBuilder();
    octahedronSolid(
      builder,
      {
        color: hexToLinear('#bca267'),
        surface: SURFACE.cloth,
        edgeId: 0,
        extent: [1, 1],
        strokeAngle: 0,
      },
      { origin: [0, 0, 0], yaw: 0 },
      [0, 0, 0],
      0.009
    );
    const grainsPerThrow = 18,
      geometry = builder.build();
    streetInstances(geometry, feeding.length * grainsPerThrow, 1860);
    const grains = new InstancedMesh(
      geometry,
      ctx.materials.painted({ instanced: true }),
      feeding.length * grainsPerThrow
    );
    grains.name = 'thrown-pigeon-grain';
    grains.frustumCulled = false;
    grains.instanceMatrix.setUsage(DynamicDrawUsage);
    const base = new Matrix4(),
      grainMatrix = new Matrix4(),
      scale = new Vector3(),
      hand = new Vector3();
    const grips = figures.map((figure) => {
      const joint = asset.bones.indexOf('hand.R'),
        lod = figure.lods[0]!,
        centre = new Vector3();
      let count = 0;
      for (let v = 0; v < lod.vertices; v++)
        if (lod.joints[v * 4] === joint && lod.weights[v * 4] === 255) {
          centre.add(
            new Vector3(...skinnedVertex(asset, lod, v, clipPose(asset.clips['FeedBirds']!, 0.48)))
          );
          count++;
        }
      return centre.multiplyScalar(1 / Math.max(1, count));
    });
    let day = true;
    const animated = {
      id: 'dam-bird-feeding',
      setTimeOfDay(_minutes: number, activity: number) {
        day = activity > 0.05;
      },
      update(seconds: number) {
        batch.begin();
        feeding.forEach((feed, i) => {
          const figure = i % figures.length,
            [x, y] = feed.citizen,
            ground = ctx.site.queries.groundHeightAt(feed.citizen);
          base.makeRotationY(feed.heading).scale(scale.setScalar(0.98)).setPosition(x, ground, -y);
          if (day) batch.draw(figure, base, clipPoseAt(asset.clips['FeedBirds']!, seconds));
          hand.copy(grips[figure]!).applyMatrix4(base);
          const into = (((seconds % 3) + 3) % 3) - 1.44;
          for (let k = 0; k < grainsPerThrow; k++) {
            const t = Math.min(1, Math.max(0, into / 0.6)),
              angle = k * 2.399,
              spread = 0.18 + (k % 5) * 0.05;
            const endX = feed.food[0] + Math.cos(angle) * spread,
              endY = ctx.site.queries.groundHeightAt(feed.food) + 0.012,
              endZ = -feed.food[1] + Math.sin(angle) * spread;
            grainMatrix
              .makeScale(1, 1, 1)
              .setPosition(
                hand.x + (endX - hand.x) * t,
                hand.y + (endY - hand.y) * t + Math.sin(Math.PI * t) * 0.35,
                hand.z + (endZ - hand.z) * t
              );
            if (!day || into < 0 || into > 1) grainMatrix.scale(scale.setScalar(0));
            grains.setMatrixAt(i * grainsPerThrow + k, grainMatrix);
          }
        });
        grains.instanceMatrix.needsUpdate = true;
        batch.end();
      },
    };
    animated.update(0);
    return {
      objects: [batch.mesh, grains],
      animated: [animated],
      obstacles: [],
      counts: { pigeonFeeders: feeding.length },
    };
  },
};
